import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { useDocumentHeaderLogo } from '../settings/settings.queries';
import { useIpdBill, useIpdDaycareAdmission } from './ipd-daycare.queries';
import { amountInWords, formatIpdDateTime } from './ipd-daycare.utils';

const RECEIPT_HEADER_NAME = 'opd_prescription';

export const IpdReceiptPrintPage = () => {
  const { admissionId, billId, paymentId } = useParams();
  const hospitalTitle = useHospitalTitle();
  const admissionQuery = useIpdDaycareAdmission(admissionId);
  const billQuery = useIpdBill(billId);
  const headerLogoQuery = useDocumentHeaderLogo(RECEIPT_HEADER_NAME);

  const payment = billQuery.data?.payments.find((entry) => entry.id === paymentId) ?? null;

  useEffect(() => {
    if (admissionQuery.data && payment && headerLogoQuery.isFetched) window.setTimeout(() => window.print(), 300);
  }, [admissionQuery.data, payment, headerLogoQuery.isFetched]);

  if (admissionQuery.isLoading || billQuery.isLoading) return <LoadingState />;
  if (admissionQuery.isError || !admissionQuery.data) return <ErrorState title="Admission not found" message="The admission could not be loaded." />;
  if (billQuery.isError || !billQuery.data) return <ErrorState title="Bill not found" message="The bill could not be loaded." />;
  if (!payment) return <ErrorState title="Receipt not found" message="This payment could not be found on the bill." />;

  const admission = admissionQuery.data;
  const bill = billQuery.data;
  const patient = admission.patientSnapshot;
  const age = `${patient.ageYears}Y ${patient.ageMonths}M ${patient.ageDays}D`;
  const address = [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ') || '-';
  const logo = headerLogoQuery.data;

  return (
    <div className="print-receipt-page">
      {logo ? <div className="print-receipt-logo"><img src={logo} alt={hospitalTitle} /></div> : null}
      <p className="print-receipt-heading">Money Receipt</p>

      <table className="print-receipt-info">
        <tbody>
          <tr>
            <td>
              <p>UHID NO : <b>{patient.uhid ?? '-'}</b></p>
              <p>PATIENT NAME : <b>{patient.name}</b></p>
              <p>GENDER : <b>{patient.gender}</b> &nbsp; &nbsp; &nbsp; &nbsp;AGE : <b>{age}</b></p>
              <p>MOB NO : <b>{patient.phone ?? '-'}</b></p>
            </td>
            <td>
              <span className="print-receipt-barcode" aria-hidden="true" />
              <p>Transaction ID : {payment.id}</p>
              <p>Date : {formatIpdDateTime(payment.date)}</p>
              <p>Address : {address}</p>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="print-receipt-thanks">
        <div className="print-receipt-thanks-text">
          <p className="print-receipt-thanks-title">Thank You!</p>
          <p className="print-receipt-thanks-sub">Your payment has been received.</p>
        </div>
        <div className="print-receipt-amount">
          <p className="print-receipt-amount-value">Amount : {payment.amount}/-</p>
          <p className="print-receipt-amount-words">{amountInWords(payment.amount)}</p>
        </div>
        <p className="print-receipt-for">*** Bill No {bill.billNumber} ***</p>
      </div>

      <table className="print-receipt-signoff">
        <tbody>
          <tr>
            <th>Payment Received By : {payment.createdBy.name}</th>
            <th>Authorized Signature</th>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
